import { css } from '@emotion/react';

import { useTranslation } from 'next-i18next';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';

import { useScrollPosition } from '@hooks/useScrollPosition';
import { FontSizes, Radius, Spacing } from 'chatie-design-system';

import { DecoratedButton } from '@components/common/Button';
import Empty from '@components/common/Empty';
import { PaginationIterator } from '@components/common/Pagination';

import { fetchMyStories, fetchUserStories } from '@api/story';
import { MarketItemType, Platform, StoryTheme } from '@constants';
import { useGlobalContext } from '@contexts/global';
import { IMyStory, IStory } from '@model';
import { useUserMarketItemInventory } from '@utils/hook';
import { usePaginationQuery } from '@utils/query';
import { useWebView } from '@utils/webview';

import Story from './Story';

const NativeInFeedAd = dynamic(
  () => import('@components/common/NativeInFeedAd'),
  { ssr: false }
);

const writeButtonCSS = css`
  padding: ${Spacing.medium}px;
  button {
    box-shadow: none;
    font-weight: 400;
  }
`;
const userButtonCSS = css`
  width: 100%;
  font-size: ${FontSizes.small}px;
  font-weight: 600;
  flex: 1 1 0px;
  height: 48px;
  border-radius: ${Radius.medium}px;
`;
interface StoryListProps {
  userId: number;
  isMyPage?: boolean;
}

const StoryList = ({ userId, isMyPage }: StoryListProps) => {
  const { t } = useTranslation();
  const { web } = useWebView();
  const { platform } = useGlobalContext();
  const router = useRouter();

  const { data, getDecoration } = useUserMarketItemInventory(userId);
  const profileBtnDecoration = getDecoration(data, MarketItemType.profileBtn);

  const myStoryQuery = usePaginationQuery(
    ['myStoryList', userId],
    ({ pageParam }) => fetchMyStories(undefined, pageParam),
    {
      enabled: !!isMyPage,
      refetchOnWindowFocus: 'always',
    }
  );

  useScrollPosition(`userStoryList:${userId}`);

  const userStoryQuery = usePaginationQuery(
    ['userStoryList', userId],
    ({ pageParam }) => fetchUserStories(userId, pageParam),
    {
      enabled: !isMyPage,
      refetchOnWindowFocus: 'always',
    }
  );

  useScrollPosition(`userStoryList:${userId}`);

  const handleNavigateStudio = () => {
    if (platform === Platform.web) {
      router.push('/studio/stories/create');
    }
    web.openPage('/studio/stories/create');
  };

  const handleNavigateStoryDetail = (story: IStory | IMyStory) => {
    const url = isMyPage
      ? `/studio/stories/${story.id}`
      : story.theme === StoryTheme.iknow
      ? `/stories/${story.id}/licensed`
      : `/stories/${story.id}`;

    if (platform === Platform.web) {
      router.push(url);
    }
    web.openPage(url);
  };

  return (
    <>
      {isMyPage && (
        <div css={writeButtonCSS}>
          <DecoratedButton
            css={userButtonCSS}
            decoration={profileBtnDecoration}
            onClick={handleNavigateStudio}
          >
            새로운 작품쓰기
          </DecoratedButton>
        </div>
      )}
      {isMyPage ? (
        <PaginationIterator name="user-story-list" query={myStoryQuery} border>
          {(item) => (
            <Story
              {...item}
              onClick={() => handleNavigateStoryDetail(item)}
              isMyPage={true}
            />
          )}
        </PaginationIterator>
      ) : (
        <PaginationIterator
          name="user-story-list"
          query={userStoryQuery}
          border
          inFeedAd={<NativeInFeedAd unitKey="home_cp_mobile" />}
        >
          {(item) => (
            <Story {...item} onClick={() => handleNavigateStoryDetail(item)} />
          )}
        </PaginationIterator>
      )}

      <Empty query={isMyPage ? myStoryQuery : userStoryQuery}>
        <p>{t('작품이 없습니다.')}</p>
      </Empty>
    </>
  );
};

export default StoryList;
